import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthProvider";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { staticProducts } from "@/data/staticData";

const WISHLIST_STORAGE_KEY = "wishlist_items";

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
    const { getToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [wishlist, setWishlist] = useState([]);
    const token = getToken();
    const { t } = useTranslation();

    // Load wishlist from localStorage on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const storedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
            if (storedWishlist) {
                try {
                    const parsed = JSON.parse(storedWishlist);
                    setWishlist(Array.isArray(parsed) ? parsed : []);
                } catch (error) {
                    console.error('Error parsing wishlist from localStorage:', error);
                    setWishlist([]);
                }
            }
        }
    }, []);

    // Save wishlist to localStorage whenever it changes
    useEffect(() => {
        if (typeof window !== 'undefined' && wishlist.length >= 0) {
            localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
        }
    }, [wishlist]);

    const getWishList = async () => {
        // Load from localStorage instead of API
        if (typeof window !== 'undefined') {
            const storedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
            if (storedWishlist) {
                try {
                    const parsed = JSON.parse(storedWishlist);
                    setWishlist(Array.isArray(parsed) ? parsed : []);
                } catch (error) {
                    console.error('Error parsing wishlist:', error);
                    setWishlist([]);
                }
            } else {
                setWishlist([]);
            }
        }
    };

    const addToWishList = async (id) => {
        if (!token) {
            return toast.error(t("alerts.Login_required") || "يجب تسجيل الدخول أولاً");
        }
        
        setLoading(true);
        try {
            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Find product from static data
            const product = staticProducts.find(p => p.id === parseInt(id));
            
            if (!product) {
                toast.error("المنتج غير موجود");
                return;
            }

            // Check if already in wishlist
            const isAlreadyInWishlist = wishlist.some(item => item.id === parseInt(id));
            
            if (isAlreadyInWishlist) {
                toast.info(t("alerts.alreadyInWishList") || "المنتج موجود بالفعل في المفضلة");
                return;
            }

            // Add to wishlist
            const newWishlist = [...wishlist, product];
            setWishlist(newWishlist);
            
            toast.success(t("alerts.addToWishList") || "تمت إضافة المنتج إلى المفضلة بنجاح");
        } catch (error) {
            console.error(error);
            toast.error(t("alerts.faildToAddToWishList") || "فشل إضافة المنتج إلى المفضلة");
        } finally {
            setLoading(false);
        }
    };

    const deleteFromWhisList = async (id) => {
        if (!token) {
            return toast.error(t("alerts.Login_required") || "يجب تسجيل الدخول أولاً");
        }
        
        setLoading(true);
        try {
            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Remove from wishlist
            const newWishlist = wishlist.filter(item => item.id !== parseInt(id));
            setWishlist(newWishlist);
            
            toast.success(t("alerts.removeFromWishList") || "تمت إزالة المنتج من المفضلة بنجاح");
        } catch (error) {
            console.error(error);
            toast.error(t("alerts.falidToRemoveFromWishList") || "فشل إزالة المنتج من المفضلة");
        } finally {
            setLoading(false);
        }
    };

    // Load wishlist when token changes
    useEffect(() => {
        if (token) {
            getWishList();
        } else {
            // Clear wishlist if user logs out
            setWishlist([]);
            if (typeof window !== 'undefined') {
                localStorage.removeItem(WISHLIST_STORAGE_KEY);
            }
        }
    }, [token]);

    return (
        <WishlistContext.Provider value={{ wishlist, getWishList, addToWishList, loading, deleteFromWhisList }}>
            {children}
        </WishlistContext.Provider>
    );
};

export const useWishlist = () => {
    return useContext(WishlistContext);
};
